import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/common";

const STEPS = ["submitted", "reviewing", "label_sent", "outcome", "resolved"];
const STEP_NAMES = ["Received", "Reviewing", "Label sent", "Decision", "Complete"];
const reached = (c: any) => {
  const seen = new Set((c.timeline || []).map((t: any) => (["covered", "quoted", "declined"].includes(t.status) ? "outcome" : t.status)));
  return STEPS.map((s) => seen.has(s));
};

export function ClaimCard({ c }: { c: any }) {
  const done = reached(c);
  return (
    <div className="border border-neutral-200 bg-white p-5 md:p-6 space-y-5" data-testid={`claim-card-${c.number}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div><p className="eyebrow text-[10px] text-neutral-500">Claim {c.number} · Order {c.order_number}</p><p className="font-display text-xl mt-1">{c.item}</p></div>
        <p className="text-sm" data-testid="claim-card-status"><span className="text-neutral-500">Status: </span><strong>{c.status_label}</strong>{c.status === "quoted" && c.quote ? ` · A$${Number(c.quote).toLocaleString()}` : ""}</p>
      </div>
      <ol className="grid grid-cols-5 gap-1 text-[10px] uppercase tracking-wider text-center">
        {STEP_NAMES.map((n, i) => (
          <li key={n} className={done[i] ? "text-neutral-900" : "text-neutral-400"}>
            <span className={`mx-auto mb-1.5 grid place-items-center h-6 w-6 rounded-full border ${done[i] ? "bg-neutral-900 border-neutral-900 text-white" : "border-neutral-300"}`}>{done[i] ? <Check size={12} /> : i + 1}</span>{n}
          </li>
        ))}
      </ol>
      <ul className="text-sm space-y-2 border-t border-neutral-100 pt-4">
        {[...(c.timeline || [])].reverse().map((t: any, i: number) => (
          <li key={i}><span className="text-neutral-500 text-xs">{new Date(t.at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span> · {t.label}{t.note && <p className="text-neutral-600 text-xs mt-0.5">{t.note}</p>}</li>
        ))}
      </ul>
    </div>
  );
}

export function ClaimLookup() {
  const [f, setF] = useState({ number: "", email: "" });
  const [c, setC] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    try { setC((await api.post("/warranty/status", f)).data); } catch (x) { setC(null); toast.error(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-10 space-y-4" data-testid="claim-lookup">
      <h3 className="font-display uppercase tracking-[0.08em] text-xl text-center">Check a Claim</h3>
      <form onSubmit={submit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <Field label="Claim number"><input required value={f.number} onChange={(e) => setF({ ...f, number: e.target.value })} placeholder="WC-123456" className={inputCls} data-testid="claim-lookup-number" /></Field>
        <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="claim-lookup-email" /></Field>
        <button disabled={busy} className="btn-ink px-7 py-3.5 text-[11px] tracking-[0.25em] uppercase disabled:opacity-60" data-testid="claim-lookup-submit">{busy ? "Checking…" : "Check"}</button>
      </form>
      {c && <ClaimCard c={c} />}
    </div>
  );
}

export function MyClaims() {
  const [items, setItems] = useState<any[] | null>(null);
  useEffect(() => { api.get("/account/warranty-claims").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  if (!items) return null;
  if (!items.length) return <p className="text-sm text-neutral-600" data-testid="my-claims-empty">You haven't made any warranty claims. <a href="/pages/warranty#warranty-claim" className="underline">Start a claim</a></p>;
  return <div className="space-y-5" data-testid="my-claims">{items.map((c) => <ClaimCard key={c.number} c={c} />)}</div>;
}
